import { useCallback, useEffect, useState } from 'react';

import FlatButton from '@components/flat-button/flat-button';
import BasicButton from '@components/basic-button/basic-button.component';
import { FullViewDialog } from '@components/full-view-dialog/full-view-dialog';
import { DialogHeader } from '@components/full-view-dialog/dialog-header';
import { DialogContent } from '@components/full-view-dialog/dialog-content';
import { Divider } from '@components/divider/divider';
import CustomInput from '@components/custom-input/custom-input.component';
import { useForm } from '@hooks/form';
import { CreateService, Service, UpdateService } from '@app-types/service.type';
import { validateSchema } from '@utils/validate-schema';

import { ServiceCreationPerkList } from '../../containers/service-creation-perk-list/service-creation-perk-list';
import { AddPerkInput } from '../../components/add-perk-input/add-perk-input';
import { createServiceSchema, updateServiceSchema } from '../../config';

import styles from './service-dialog.module.scss';

type Props = {
    shown: boolean;
    service?: Service;
    loading: boolean;
    onClose: () => void;
    onCreate: (data: CreateService) => Promise<boolean>;
    onUpdate: (data: UpdateService) => void;
};

const ServiceDialog = ({
    shown,
    service,
    loading,
    onClose,
    onCreate,
    onUpdate,
}: Props) => {
    const [perks, setPerks] = useState<string[]>([]);
    const [errors, setErrors] = useState<Map<string, string>>(new Map());

    const { formData, setFormData, handleChange } = useForm({
        defaultValues: {
            name: '',
            description: '',
            code: '',
            totalFreeTowingServices: 0,
            totalFreeAmbulanceServices: 0,
            towingEligibilityDays: 0,
        },
    });

    useEffect(() => {
        if (!service) return;
        setFormData({
            name: service.name,
            description: service.description ?? '',
            code: service.code ?? '',
            totalFreeTowingServices: service.totalFreeTowingServices,
            totalFreeAmbulanceServices: service.totalFreeAmbulanceServices,
            towingEligibilityDays: service.towingEligibilityDays ?? 0,
        });
        if (service.perks) {
            setPerks(service.perks.map((p) => p.name));
        }
    }, [service]);

    const addPerk = (text: string): boolean => {
        if (perks.includes(text)) return false;
        setPerks([...perks, text]);
        return true;
    };

    const deletePerk = (id: number) => {
        const filtered = perks.filter((_, idx) => idx !== id);
        setPerks(filtered);
    };

    const clearForm = () => {
        setFormData({
            name: '',
            description: '',
            code: '',
            totalFreeTowingServices: 0,
            totalFreeAmbulanceServices: 0,
            towingEligibilityDays: 0,
        });
    };

    const onSubmit = () => {
        setErrors(new Map());
        if (!service) {
            return createService();
        }
        // update service
        updateService();
    };

    const createService = async () => {
        const payload: CreateService = {
            name: formData.name,
            description: formData.description,
            code: formData.code,
            totalFreeTowingServices: Number(formData.totalFreeTowingServices),
            totalFreeAmbulanceServices: Number(
                formData.totalFreeAmbulanceServices
            ),
            towingEligibilityDays: Number(formData.towingEligibilityDays),
        };
        if (perks.length > 0) {
            payload.perks = perks.map((perk, idx) => ({
                order: idx + 1,
                name: perk,
            }));
        }
        const errors = validateSchema(payload, createServiceSchema);
        if (errors) return setErrors(errors);

        const created = await onCreate(payload);
        if (!created) return;

        setPerks([]);
        clearForm();
    };

    const updateService = () => {
        const payload: UpdateService = {
            name: formData.name,
            description: formData.description,
            code: formData.code,
            totalFreeTowingServices: Number(formData.totalFreeTowingServices),
            totalFreeAmbulanceServices: Number(
                formData.totalFreeAmbulanceServices
            ),
            towingEligibilityDays: Number(formData.towingEligibilityDays),
        };
        if (perks.length > 0) {
            payload.perks = perks.map((perk, idx) => ({
                order: idx + 1,
                name: perk,
            }));
        }
        const errors = validateSchema(payload, updateServiceSchema);
        if (errors) return setErrors(errors);
        onUpdate(payload);
    };

    const showError = useCallback(
        (name: string) => {
            if (!errors.has(name)) return <></>;
            return <span className={styles.error}>{errors.get(name)}</span>;
        },
        [errors]
    );

    return (
        <FullViewDialog shown={shown} onClose={onClose} maxWidth={600}>
            <DialogHeader>
                <h3 className={styles.title}>Crear servicio</h3>
                <label className={styles.subtitle}>
                    Formulario para la creación de un nuevo servicio
                </label>
            </DialogHeader>
            <DialogContent>
                <Divider />
                <form className={styles.form}>
                    <div className={styles.formRow}>
                        <label>Nombre del servicio</label>
                        <CustomInput
                            width="100%"
                            placeholder="Nombre del servicio"
                            type="text"
                            value={formData.name}
                            onChange={handleChange('name')}
                        />
                        {showError('name')}
                    </div>
                    <div className={styles.formRow}>
                        <label>Descripción</label>
                        <CustomInput
                            width="100%"
                            placeholder="Descripción"
                            type="text"
                            value={formData.description}
                            onChange={handleChange('description')}
                        />
                        {showError('description')}
                    </div>
                    <div className={styles.formRow}>
                        <label>Código del servicio (opcional)</label>
                        <CustomInput
                            width="100%"
                            placeholder="Código del servicio"
                            type="text"
                            value={formData.code}
                            onChange={handleChange('code')}
                        />
                        {showError('code')}
                    </div>
                    <div className={styles.formCol}>
                        <div className={styles.formRow} style={{ flex: 1 }}>
                            <label>Nº de grúas</label>
                            <CustomInput
                                type="number"
                                width="100%"
                                value={formData.totalFreeTowingServices}
                                onChange={handleChange(
                                    'totalFreeTowingServices'
                                )}
                            />
                            {showError('totalFreeTowingServices')}
                        </div>
                        <div className={styles.formRow} style={{ flex: 1 }}>
                            <label>Nº de ambulancias</label>
                            <CustomInput
                                type="number"
                                width="100%"
                                value={formData.totalFreeAmbulanceServices}
                                onChange={handleChange(
                                    'totalFreeAmbulanceServices'
                                )}
                            />
                            {showError('totalFreeAmbulanceServices')}
                        </div>
                        <div className={styles.formRow} style={{ flex: 1.4 }}>
                            <label>Nº de días para usar grúa</label>
                            <CustomInput
                                type="number"
                                width="100%"
                                value={formData.towingEligibilityDays}
                                onChange={handleChange('towingEligibilityDays')}
                            />
                            {showError('towingEligibilityDays')}
                        </div>
                    </div>
                    <Divider />
                    <div>
                        <h4 className={styles.perksTitle}>
                            Beneficios del servicio
                        </h4>
                        <AddPerkInput onAdd={addPerk} />
                        <ServiceCreationPerkList
                            perks={perks}
                            onDelete={deletePerk}
                        />
                    </div>
                </form>
                <footer className={styles.footer}>
                    <FlatButton onClick={onClose}>Descartar</FlatButton>
                    <BasicButton onClick={onSubmit} loading={loading}>
                        {service ? 'Actualizar' : 'Crear'} servicio
                    </BasicButton>
                </footer>
            </DialogContent>
        </FullViewDialog>
    );
};

export { ServiceDialog };
